import React from "react";
import { ShieldAlert } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/shared/PageHero";
import { Section } from "@/components/ui-kit/Kit";
import { Reveal } from "@/components/motion/Reveal";

export const LegalPage = ({ pageKey, path }) => {
  const { t } = useI18n();
  const p = t(`pages.${pageKey}`);
  useSEO({ title: p.title, description: p.lead, path });

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid={`legal-${pageKey}`}>
        {p.review && (
          <Reveal>
            <div className="mb-14 flex items-start gap-3 border border-gold/40 bg-gold/5 p-5">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-navy/70">
                Final legal review required — this wording is subject to approval by GPU counsel.
              </p>
            </div>
          </Reveal>
        )}
        <div className="grid gap-14 lg:grid-cols-[240px_1fr] lg:gap-20">
          <nav aria-label="On this page" className="lg:sticky lg:top-28 lg:self-start">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-navy/40">Contents</p>
            <ul className="mt-5 space-y-2.5">
              {(p.sections || []).map((s, i) => (
                <li key={s.heading}>
                  <a
                    href={`#sec-${i}`}
                    className="text-[13px] text-slate-500 transition-colors duration-200 hover:text-grn"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="space-y-14">
            {(p.sections || []).map((s, i) => (
              <Reveal key={s.heading} id={`sec-${i}`}>
                <div id={`sec-${i}`} className="scroll-mt-28">
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono text-[11px] tracking-[0.24em] text-grn">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h2 className="font-display text-xl font-semibold text-navy sm:text-2xl">{s.heading}</h2>
                  </div>
                  <div className="mt-5 space-y-4 pl-0 sm:pl-10">
                    {s.body.map((b, j) => (
                      <p key={j} className="max-w-3xl text-sm leading-relaxed text-slate-600">
                        {b}
                      </p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>
    </>
  );
};
